import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ReadAloudButton } from "./ReadAloudButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Heart, Star, Hand, MoreVertical, Trash2, Image, Video, MessageCircle, Send, ThumbsUp, Bookmark, Share2, Flag } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { TransplantBadge } from "./TransplantSettings";

export default function PostCard({ post, currentUserId, onReaction, onDelete }) {
  const { user } = useAuth();
  const [userReactions, setUserReactions] = useState([]);
  const [animating, setAnimating] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    fetchUserReactions();
    fetchCommentCount();
    checkBookmarkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  const checkBookmarkStatus = async () => {
    try {
      const response = await axios.get(`${API}/posts/${post.id}/bookmarked`);
      setIsBookmarked(response.data.bookmarked);
    } catch (error) {
      // Silently fail
    }
  };

  const handleBookmark = async () => {
    try {
      const response = await axios.post(`${API}/bookmarks`, { post_id: post.id });
      setIsBookmarked(response.data.bookmarked);
      toast.success(response.data.bookmarked ? "Post saved!" : "Removed from saved");
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const handleShare = async () => {
    try {
      await axios.post(`${API}/reposts`, { post_id: post.id, comment: "" });
      setIsShared(true);
      toast.success("Post shared to your profile!");
    } catch (error) {
      if (error.response?.data?.detail === "Already reposted") {
        toast.info("You've already shared this post");
      } else {
        toast.error("Failed to share post");
      }
    }
  };

  const fetchUserReactions = async () => {
    try {
      const response = await axios.get(`${API}/posts/${post.id}/reactions`);
      setUserReactions(response.data.reactions);
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  };

  const fetchCommentCount = async () => {
    try {
      const response = await axios.get(`${API}/posts/${post.id}/comments/count`);
      setCommentCount(response.data.count);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error("Failed to load comments");
    } finally {
      setLoadingComments(false);
    }
  };

  const handleToggleComments = () => {
    if (!showComments) {
      fetchComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API}/posts/${post.id}/comments`, {
        content: newComment,
      });
      setComments((prev) => [...prev, response.data]);
      setCommentCount((prev) => prev + 1);
      setNewComment("");
      toast.success("Comment added!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add comment";
      toast.error(message);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentCount((prev) => prev - 1);
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await axios.post(`${API}/comments/${commentId}/like`);
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, likes: response.data.liked ? (c.likes || 0) + 1 : (c.likes || 0) - 1 }
            : c
        )
      );
    } catch (error) {
      toast.error("Failed to like comment");
    }
  };

  const handleReaction = async (type) => {
    setAnimating(type);
    await onReaction(post.id, type);

    // Update local reaction state
    if (userReactions.includes(type)) {
      setUserReactions((prev) => prev.filter((r) => r !== type));
    } else {
      setUserReactions((prev) => [...prev, type]);
    }

    setTimeout(() => setAnimating(null), 300);
  };

  // Construct full media URL
  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const submitReport = async (reason) => {
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/reports`, {
        target_type: "post",
        target_id: post.id,
        reason,
        details: "",
      });
      toast.success("Report submitted. Thank you for keeping HappyBook safe.");
      setShowReport(false);
    } catch (e) {
      toast.error("Could not submit report. Please try again.");
    } finally {
      setSubmittingReport(false);
    }
  };

  // Construct full photo URL
  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  const isOwner = currentUserId === post.user_id;

  const reactions = [
    {
      type: "heart",
      icon: Heart,
      count: post.hearts,
      color: "text-red-500",
      bgActive: "bg-red-50",
    },
    {
      type: "star",
      icon: Star,
      count: post.stars,
      color: "text-yellow-500",
      bgActive: "bg-yellow-50",
    },
    {
      type: "hug",
      icon: Hand,
      count: post.hugs,
      color: "text-pink-500",
      bgActive: "bg-pink-50",
    },
  ];

  return (
    <article className="card-happy" data-testid={`post-card-${post.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Avatar className="w-12 h-12 border-2 border-primary/20">
            <AvatarImage src={getPhotoUrl(post.user_photo)} />
            <AvatarFallback className="bg-primary text-white font-bold">
              {post.user_name?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground">{post.user_name}</h3>
              {post.transplant_info && (
                <TransplantBadge
                  info={post.transplant_info}
                  userName={post.user_name}
                  userPhoto={getPhotoUrl(post.user_photo)}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>

        {isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid={`post-menu-btn-${post.id}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={`delete-post-btn-${post.id}`}
                onClick={() => onDelete(post.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Post
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid={`post-menu-btn-${post.id}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={`report-post-btn-${post.id}`}
                onClick={() => setShowReport(true)}
                className="text-destructive focus:text-destructive"
              >
                <Flag className="w-4 h-4 mr-2" />
                Report Post
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className="text-foreground leading-relaxed whitespace-pre-wrap">
          {post.content}
        </p>
      </div>

      {/* Media */}
      {post.media_url && post.media_type === "photo" && (
        <div className="mb-4 rounded-2xl overflow-hidden bg-muted">
          <img
            src={post.media_url}
            alt="Post media"
            className="w-full h-64 object-cover"
            loading="lazy"
          />
        </div>
      )}

      {post.media_url && post.media_type === "video" && (
        <div className="mb-4 rounded-2xl overflow-hidden bg-muted">
          <video
            src={post.media_url}
            className="w-full h-64 object-cover"
            controls
          />
        </div>
      )}

      {/* Media type indicator */}
      {post.media_type !== "text" && !post.media_url && (
        <div className="mb-4 flex items-center gap-2 text-muted-foreground">
          {post.media_type === "photo" ? (
            <Image className="w-4 h-4" />
          ) : (
            <Video className="w-4 h-4" />
          )}
          <span className="text-sm capitalize">{post.media_type} post</span>
        </div>
      )}

      {/* Reactions & Comments */}
      <div className="flex items-center gap-3 pt-3 border-t border-border">
        {reactions.map((reaction) => {
          const Icon = reaction.icon;
          const isActive = userReactions.includes(reaction.type);
          const isAnimating = animating === reaction.type;

          return (
            <button
              key={reaction.type}
              data-testid={`reaction-btn-${reaction.type}-${post.id}`}
              onClick={() => handleReaction(reaction.type)}
              className={`reaction-btn flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                isActive ? reaction.bgActive : "hover:bg-muted"
              } ${isAnimating ? "scale-125" : ""}`}
            >
              <Icon
                className={`w-5 h-5 transition-all ${
                  isActive ? reaction.color : "text-muted-foreground"
                } ${isActive ? "fill-current" : ""}`}
              />
              <span
                className={`text-sm font-medium ${
                  isActive ? reaction.color : "text-muted-foreground"
                }`}
              >
                {reaction.count}
              </span>
            </button>
          );
        })}

        {/* Comment Button */}
        <button
          onClick={handleToggleComments}
          data-testid={`comment-btn-${post.id}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            showComments ? "bg-blue-50 text-blue-500" : "hover:bg-muted text-muted-foreground"
          }`}
        >
          <MessageCircle className={`w-5 h-5 ${showComments ? "fill-current" : ""}`} />
          <span className="text-sm font-medium">{commentCount}</span>
        </button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Share Button */}
        <button
          onClick={handleShare}
          data-testid={`share-btn-${post.id}`}
          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
            isShared ? "bg-purple-50 text-purple-500" : "hover:bg-muted text-muted-foreground"
          }`}
          title="Share"
        >
          <Share2 className="w-5 h-5" />
        </button>

        {/* Bookmark Button */}
        <button
          onClick={handleBookmark}
          data-testid={`bookmark-btn-${post.id}`}
          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${
            isBookmarked ? "bg-amber-50 text-amber-500" : "hover:bg-muted text-muted-foreground"
          }`}
          title={isBookmarked ? "Remove from saved" : "Save post"}
        >
          <Bookmark className={`w-5 h-5 ${isBookmarked ? "fill-current" : ""}`} />
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-border">
          {/* Comment Input */}
          <form onSubmit={handleSubmitComment} className="flex gap-2 mb-4">
            <Avatar className="w-8 h-8 flex-shrink-0">
              <AvatarImage src={getPhotoUrl(user?.profile_photo)} />
              <AvatarFallback className="bg-primary text-white text-xs">
                {user?.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a positive comment..."
                className="flex-1 rounded-full bg-muted/50"
                data-testid={`comment-input-${post.id}`}
              />
              <Button
                type="submit"
                size="icon"
                disabled={!newComment.trim() || submittingComment}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid={`submit-comment-btn-${post.id}`}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>

          {/* Comments List */}
          {loadingComments ? (
            <div className="text-center py-4 text-muted-foreground">
              Loading comments...
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No comments yet. Be the first to share your thoughts!
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="flex gap-2 group"
                  data-testid={`comment-${comment.id}`}
                >
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={getPhotoUrl(comment.user_photo)} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xs">
                      {comment.user_name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="bg-muted/50 rounded-2xl px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{comment.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-foreground mt-1">{comment.content}</p>
                    </div>
                    <div className="flex items-center gap-3 mt-1 ml-2">
                      <button
                        onClick={() => handleLikeComment(comment.id)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-blue-500 transition-colors"
                        data-testid={`like-comment-btn-${comment.id}`}
                      >
                        <ThumbsUp className="w-3 h-3" />
                        <span>{comment.likes || 0}</span>
                      </button>
                      <ReadAloudButton 
                        text={comment.content}
                        size="icon"
                        variant="ghost"
                        className="opacity-50 hover:opacity-100"
                      />
                      {comment.user_id === currentUserId && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-xs text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          data-testid={`delete-comment-btn-${comment.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent data-testid={`report-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle>Report Post</DialogTitle>
            <DialogDescription>
              Why are you reporting this post? Reports help us keep HappyBook safe.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            {[
              { id: "child_safety", label: "Child safety concern" },
              { id: "sexual_content", label: "Sexual or explicit content" },
              { id: "harassment", label: "Harassment or bullying" },
              { id: "violence", label: "Violence or threats" },
              { id: "hate_speech", label: "Hate speech" },
              { id: "spam", label: "Spam or scam" },
              { id: "other", label: "Something else" },
            ].map((r) => (
              <Button
                key={r.id}
                variant="outline"
                disabled={submittingReport}
                onClick={() => submitReport(r.id)}
                data-testid={`report-reason-${r.id}`}
                className="justify-start"
              >
                <Flag className="w-4 h-4 mr-2 text-muted-foreground" />
                {r.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}
